import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { AsyncSearch, SearchableSelect } from 'components';
import { TextField, TextFieldRef, ValidationResult, ValidationStatus } from 'components/inputs/text-field';
import { useAuth, useCustomer } from 'contexts';
import { useCustomerAPI } from 'hooks/use-customer-api';
import useTimeZone from 'hooks/use-timezone';
import { getBookingByNumber, getLocations } from 'services';
import { Customer, Location, Role, sslScacs } from 'types';
import { mapById } from 'utils';

import { useNewBooking } from './new-booking';

const NewGeneral: FC = () => {
  const { pathname } = useLocation();
  const { templateName } = useParams();
  const { searchCustomers } = useCustomerAPI();
  const { userProfile } = useAuth();
  const { customers } = useCustomer();
  const { dateInputToUtc, utcToZonedDate } = useTimeZone();

  const { booking, setBooking } = useNewBooking();
  const bookingNumberRef = useRef<TextFieldRef>(null);

  const [locations, setLocations] = useState<Map<number, Location>>(new Map());
  useEffect(() => {
    getLocations().then(mapById).then(setLocations);
  }, [setLocations]);

  const isNewBooking = useMemo(() => pathname.includes('bookings/new'), [pathname]);

  const validateBookingNumber = useCallback(
    async (bookingNumber: string): Promise<ValidationResult> => {
      if (!isNewBooking || !bookingNumber) {
        return { status: ValidationStatus.DEFAULT };
      } else if (!booking.customer?.id) {
        return { status: ValidationStatus.DEFAULT, error: 'Account required for validation' };
      } else if (!booking.location?.id) {
        return { status: ValidationStatus.DEFAULT, error: 'Location required for validation' };
      } else if (await getBookingByNumber(bookingNumber, booking.customer.id, booking.location.id)) {
        return { status: ValidationStatus.WARNING, error: 'Booking number already in use by this customer' };
      } else {
        return { status: ValidationStatus.SUCCESS };
      }
    },
    [booking.customer?.id, booking.location?.id, isNewBooking]
  );

  return (
    <div className="card-primary">
      <div className="card-header px-3">
        <h4>GENERAL INFORMATION</h4>
      </div>
      <div className="card-body">
        <div className="grid-4 p-2">
          <div className="position-relative">
            <h6 className="text-info-dark">{isNewBooking ? 'BOOKING NUMBER' : 'TEMPLATE NAME'}</h6>
            <TextField
              className="mt-2"
              disabled={!isNewBooking}
              required={isNewBooking}
              value={(isNewBooking ? booking.bookingNumber : templateName) ?? ''}
              onChange={bn => setBooking(b => ({ ...b, bookingNumber: bn.substring(0, 20) }))}
              validate={validateBookingNumber}
              ref={bookingNumberRef}
            />
          </div>
          <div>
            <h6 className="text-info-dark">ACCOUNT</h6>
            {[Role.ADMIN_CUSTOMER, Role.CUSTOMER, Role.EXTERNAL].includes(userProfile.role) ? (
              <div className="mt-2 w-100">
                <SearchableSelect
                  required
                  options={customers.map(({ id, name }) => ({ label: name, value: `${id}` }))}
                  value={`${booking?.customer?.id ?? ''}`}
                  onValueSelected={customerId => {
                    setBooking(b => ({ ...b, customer: customers.find(({ id }) => `${id}` === customerId) }));
                    bookingNumberRef.current?.revalidate(); /* force revalidate booking number on customer change */
                  }}
                  data-cy="new-template-account-search"
                />
              </div>
            ) : (
              <div className="mt-2 w-100">
                <AsyncSearch<Customer>
                  required
                  formatOption={({ name }) => name}
                  formatValue={customer => customer?.name ?? ''}
                  handleSelect={customer => {
                    setBooking(b => ({ ...b, customer, location: customer?.location as Location | undefined }));
                    bookingNumberRef.current?.revalidate(); /* force revalidate booking number on customer change */
                  }}
                  search={searchCustomers}
                  value={booking.customer}
                  data-cy="new-template-account-search"
                />
              </div>
            )}
          </div>
          <div className="col-span-2 row-span-3 h-100 d-flex flex-column">
            <h6 className="text-info-dark">NOTES</h6>
            <textarea
              className="mt-2 w-100 flex-fill"
              onChange={e => setBooking(p => ({ ...p, notes: e.target.value }))}
              value={booking.notes ?? ''}
            />
          </div>
          <div>
            <h6 className="text-info-dark">LOCATION</h6>
            <div className="mt-2 w-100">
              <SearchableSelect
                disabled={!!booking.customer?.location}
                required={isNewBooking}
                options={[...locations.values()].map(({ id, name }) => ({ label: name, value: `${id}` }))}
                onValueSelected={id => {
                  setBooking(b => ({ ...b, location: locations.get(Number(id)) }));
                  bookingNumberRef.current?.revalidate(); /* force revalidate booking number on location change */
                }}
                value={`${booking.location?.id ?? ''}`}
                data-cy="new-template-location-search"
              />
            </div>
          </div>
          <div className="position-relative">
            <h6 className="text-info-dark">RAILYARD</h6>
            <input
              type="text"
              className="mt-2 w-100 text-uppercase"
              disabled={!isNewBooking}
              defaultValue={booking.railyard ?? ''}
              onChange={e => setBooking(b => ({ ...b, railyard: e.target.value }))}
              data-cy="new-template-railyard-input"
            />
          </div>
          {isNewBooking && (
            <>
              <div>
                <h6 className="text-info-dark">START DATE</h6>
                <input
                  type="date"
                  className="mt-2 w-100"
                  onChange={e => setBooking(p => ({ ...p, startDate: dateInputToUtc(e.target.value) }))}
                  value={utcToZonedDate(booking.startDate)}
                  max={utcToZonedDate(booking.expirationDate)}
                  required={isNewBooking}
                  data-cy="new-template-start-date-picker"
                />
              </div>
              <div>
                <h6 className="text-info-dark">EXPIRATION DATE</h6>
                <input
                  type="date"
                  className="mt-2 w-100"
                  onChange={e => setBooking(p => ({ ...p, expirationDate: dateInputToUtc(e.target.value) }))}
                  value={utcToZonedDate(booking.expirationDate)}
                  min={utcToZonedDate(booking.startDate)}
                  required={isNewBooking}
                  data-cy="new-template-expiration-date-picker"
                />
              </div>
            </>
          )}
          <div>
            <h6 className="text-info-dark">SSL SCAC</h6>
            <SearchableSelect
              options={sslScacs.map(scac => ({ label: scac, value: scac }))}
              value={booking?.sslScac ?? ''}
              onValueSelected={scac => setBooking(b => b && { ...b, sslScac: scac })}
              data-cy="new-template-ssl-scac-search"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewGeneral;
